import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { LegalContainer, Heading, SubHeading, Content } from './styled';
import Footer from '@src/components/common/Footer';

const Legal: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Handle hash navigation for #terms
    if (location.hash === '#terms') {
      const termsElement = document.getElementById('terms');
      if (termsElement) {
        termsElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location.hash]);

  return (
    <LegalContainer>
      <Heading>CONVIVA TERMS OF USE</Heading>

      <SubHeading>Effective as of February 23, 2023</SubHeading>

      <Content>
        <p>
          <b>Welcome to Conviva!</b>
        </p>

        <p>
          Conviva’s products and services analyze, measure and optimize the delivery of video over
          the Internet. You are visiting Conviva’s Web user interface to access Conviva’s products
          and services. By registering for and using any of the Conviva products or services,
          including applications, libraries, software development kits, other software, websites or
          other properties owned or operated by Conviva or by registering for a Conviva account
          (collectively, the <b>&ldquo;Services&rdquo;</b>), you are agreeing to be bound by these
          terms and conditions, called the Conviva Terms of Use <b>(&ldquo;Terms&rdquo;)</b>, which
          outline the terms and conditions regarding your use of the Services. These Terms are a
          legally binding contract between you and Conviva (
          <b>&ldquo;Conviva&rdquo;, &ldquo;we&rdquo;</b> or <b>&ldquo;us&rdquo;</b>), so please read
          them carefully. If you do not agree with these Terms, do not register for or use any of
          the Services.
        </p>
        <p>
          To access and use the Services, you must have initially registered by creating a user
          profile, during which you accepted these Terms. Your continued use of, and access and use
          on each subsequent visit to, the Services constitutes continuing agreement to these Terms
          as they may be revised from time to time. If you are using the Services on behalf of an
          organization, you are agreeing to these Terms for that organization and promising to
          Conviva that you have the authority to bind that organization to these Terms (in which
          event, &ldquo;you&rdquo;, &ldquo;your&rdquo;, or &ldquo;Customer&rdquo; will refer to that
          organization) unless that organization has a separate binding contract in effect with
          Conviva, in which event the terms of that contract will govern your use of the Services.
        </p>

        <h2 id="terms">1. CHANGES TO THESE TERMS</h2>
        <p>
          We reserve the right to modify these Terms. We will post the most current version of these
          Terms on Conviva’s Web portal known as Pulse <b>(&ldquo;Pulse&rdquo;)</b> or you can also
          access them at{' '}
          <a href="https://pulse.conviva.com/legal/#terms">
            https://pulse.conviva.com/legal/#terms
          </a>
          . If we make material changes to these Terms, we will post the updated version of the
          Terms in Pulse and at the above link. If you do not accept the changes, you must stop
          using the Services. Your continued use of the Services after we publish our changes to
          these Terms means that you are consenting to the updated terms.
        </p>

        <h2>2. CONSENT TO ELECTRONIC COMMUNICATIONS AND SOLICITATION</h2>
        <p>
          By registering with Conviva or signing up for the Services, you understand that we may
          send you (including via email) information regarding the Services, such as: (a) notices
          about your use of the Services, including notices of violations of use; (b) updates to the
          Services and new features or products; and (c) promotional information and materials
          regarding Conviva&apos;s products and services. If you do not wish to receive promotion
          information, please visit Conviva&apos;s Communication Management Center at{' '}
          <a href="https://pages.conviva.com/preference-center.html">
            https://pages.conviva.com/preference-center.html
          </a>{' '}
          to opt-out or update your communication preferences. Notices emailed to you will be
          considered given and received when the email is sent. If you don&apos;t consent to receive
          notices (other than promotional materials) electronically, you must stop using the
          Services.
        </p>

        <h2>3. USER REGISTRATION</h2>
        <p>
          To access certain Services including the Insights Services (as defined below), you must
          have been invited by Conviva or your company&apos;s administrative lead(s), or if solely
          accessing Conviva&apos;s Developer Community, you can initiate your registration by
          visiting{' '}
          <a href="https://community.conviva.com/dpRegister/create">
            https://community.conviva.com/dpRegister/create
          </a>
          . Conviva will initially set up the applicable company administrative lead(s) (each, an
          &ldquo;Admin User&rdquo;). If accessing Insights Services, multiple Admin Users may be set
          up, the identity and number of which depend on the needs of the Customer and its video
          player configurations. Such Admin User(s) may then designate and invite regular users to
          use the Insights Services (Admin Users and regular users are referred to collectively and
          individually as <b>&ldquo;User&rdquo;</b> unless specified otherwise).
        </p>

        <p>
          When registering for any of our Services, you must create a User profile and establish
          User credentials to access and use the Services. Your credentials consist of a User ID{' '}
          <b>(&ldquo;UID&rdquo;)</b>, which must be the fixed business email address included in the
          authorization and/or invitation, and a password (together{' '}
          <b>&ldquo;registration&rdquo;</b> and <b>&ldquo;credentials&rdquo;</b>).
        </p>

        <p>
          User login credentials and requirements:
          <ul>
            <li>
              You must enter your “single-user” UID (which is a business email address) and a
              password, both of which are unique to you as a user.
            </li>
            <li>
              You may have only one email address associated with your account, although that email
              may be associated with multiple Conviva accounts with the same Customer.
            </li>
            <li>
              Your UID and password are personal to you. They may not be shared with nor disclosed
              to anyone and they may not be used by anyone other than you. Email aliases or other
              addresses associated with more than one individual may not be used for your UID.
            </li>
            <li>
              No person or entity external to your company’s employees may be given access to our
              Services or your credentials without Conviva’s prior written consent (which Conviva
              may withhold or condition in its sole discretion).
            </li>
            <li>
              In setting up your credentials we ask that you not divulge personal information, other
              than your name in part or in full, that may be revealed by the structure of your UID
              email address. We strongly recommend that you do not use the same password as you use
              for any other work-related or personal business, to minimize the impact on you in the
              event of any unauthorized access to the Insights Services by anyone other than
              yourself.
            </li>
          </ul>
          Conviva reserves the right to block or suspend access to your account if Conviva
          reasonably suspects that fraud, corporate espionage or other unauthorized access or use is
          associated with that account, for any reason, with or without your fault. Notwithstanding
          the above, Conviva may terminate these Terms and your access to your account and the
          Services at any time and for any or no reason and shall give you notice thereof.
        </p>

        <h2>4. INSIGHTS SERVICES</h2>
        <p>
          <b>a.</b> &emsp; To access and use certain of the Services, called the{' '}
          <b>&ldquo;Insights Services&rdquo;</b>, which includes but is not limited to Experience
          Insights, Precision, Experience Benchmarks, Ad Insights, Content Insights and other
          Insights Services, you must be personnel of a current or prospective customer with
          Conviva’s authorization to access the Insights Services. The Insights Services may be
          accessed through Pulse.
        </p>
        <p>
          Certain Insights Services include the use of Google Maps features and content and your use
          of such features and content is subject to the then-current versions of the: (1) Google
          Maps/Google Earth Additional Terms of Service at{' '}
          <a href="https://maps.google.com/help/terms_maps.html">
            https://maps.google.com/help/terms_maps.html
          </a>
          ; and (2) Google Privacy Policy at{' '}
          <a href="https://www.google.com/policies/privacy/">
            https://www.google.com/policies/privacy/
          </a>
          .
        </p>
        <p>
          <b>b.</b> &emsp; Social Insights Services. Social Insights Service is an analytics
          solution that provides consumption and audience intelligence for video playback on social
          platforms. Through API calls to social platforms (including Facebook, Instagram, YouTube,
          Twitter and Snapchat), metrics and metadata are obtained regarding videos posted by you on
          these social platforms. If you register for a Social Insights Services account, you will
          have the ability to authenticate one or more of your social accounts (each, a “Social
          Account”). This will enable Conviva to import data from the Social Account(s) in order to
          analyze and add certain metrics and to deliver the Social Insights Services. At all times,
          you shall remain responsible for complying with the terms of service and other policies of
          the Social Account. Authentication is necessary to provide the Social Insights Services.
          If your Social Account is not authenticated or for any reason your Social Account is
          terminated, suspended or inoperable, then Conviva will be unable to provide you with the
          Social Insights Services, for which Conviva bears no responsibility. You shall remain the
          owner of the data transferred from your authenticated Social Account(s) (“
          <b>User Content</b>”), and you hereby grant Conviva a worldwide, non-exclusive, perpetual,
          transferable, royalty-free and sublicensable right and license to use, copy, distribute,
          prepare derivative works of, modify, transfer and publish the User Content, in whole or in
          part, to provide you with the Social Insights Services and to use anonymized and
          aggregated aspects of the User Content, such that you are not identified or identifiable,
          to provide industry-level research and benchmark data across pre-defined industry
          categories. The rights you grant in this section, including the license, survive the
          termination of this Agreement and your use of the Social Insights Services.
        </p>

        <h2>5. DEVELOPER COMMUNITY AND DEVELOPER TOOLS</h2>
        <p>
          The Services include access to: (a) Conviva’s Developer Community (
          <b>&ldquo;Developer Community&rdquo;</b>), which is an online resource containing Conviva
          proprietary materials and information to assist authorized parties with integrating, and
          validating integrations of, video players with the Insights Services; and (b)
          Conviva&apos;s Developer Tools (<b>&ldquo;Developer Tools &rdquo;</b>), which is an online
          resource containing software tools and other applications used for integrating, and
          validating integrations of, video players with the Insights Services. You may access and
          use the Developer Community and the Developer Tools, including all of the materials,
          software and information contained or located therein, solely for the purposes of
          performing or supporting the integration or validation of Conviva technology with the
          video player(s) of a current or prospective Conviva customer or a Conviva-authorized
          business development partner. You may also be granted registration, access and use rights
          as either personnel of an authorized third-party developer, or personnel of a current or
          prospective Conviva customer.
        </p>

        <p>
          So that Conviva may test and validate that the video players in your device applications
          have been integrated with the Services (“Device Applications”), you may be required to
          provide to Conviva sample Device Applications or access thereto. In such case, you grant
          Conviva a limited, non-exclusive, fully-paid, royalty-free, worldwide, non-transferable
          license to use the sample Device Applications for the purposes of testing and validating
          that the video player(s) included in those Device Applications have been properly
          integrated with the Services.
        </p>

        <h2>6. GENERAL TERMS</h2>
        <p>
          If you have entered into a Customer Agreement or similar agreement for Conviva’s products
          and services <b>(&ldquo;Customer Agreement&rdquo;)</b>”), then your use of the Services
          shall be governed by and subject to the Customer Agreement (in addition to the other
          sections of these Terms) and this Section 6 shall not apply to you. If you have not
          entered into a Customer Agreement, then your use of the Services shall be governed by and
          subject to this Section 6 (in addition to the other sections of these Terms).
        </p>

        <p>
          The Services, including but not limited to all the materials, Developer Tools, software,
          statistics, documentation, white papers, order forms and other information and resources
          made available or included in, resulting from or calculated by the Services (collectively,{' '}
          <b>&ldquo;Licensed Materials&rdquo;</b>) are the proprietary and confidential information
          of Conviva and/or its licensors. Conviva hereby grants to you a limited, personal,
          non-exclusive, fully-paid, royalty-free, worldwide, non-assignable, non-transferable
          license, without the right to sublicense, to use the Licensed Materials for the time
          period determined or indicated by Conviva, subject to the following:
          <ul>
            <li>
              You may access and use the Licensed Materials only for your internal business purposes
              or, if you are an authorized third-party developer for or a consultant to a Conviva
              customer, then you may use the Services only for the benefit of that Conviva customer.
            </li>
            <li>
              You acknowledge and agree that you will not, nor shall you allow anyone else to: (i)
              decompile, disassemble, reverse engineer or attempt to reconstruct, identify or
              discover any source code, underlying ideas, underlying user interface techniques or
              algorithms of the Licensed Materials by any means whatever, or disclose any of the
              foregoing; (ii) distribute the Licensed Materials, except as expressly permitted under
              an applicable Customer Agreement; or (iii) modify, lease, lend, use for timesharing,
              service bureau, and/or application service provider purposes the Licensed Materials.
            </li>
            <li>
              You agree: (i) that the Licensed Materials are the proprietary and confidential
              information of Conviva; (ii) to keep confidential and protect the Licensed Materials
              from unauthorized dissemination (unless otherwise approved by Conviva in writing) and
              use with the same degree of care that you use to protect your own like confidential
              information but, in no event, less than a reasonable degree of care; and (iii) not to
              use the Licensed Materials for any reason other than specified in these Terms.
            </li>
            <li>
              You may not transfer or assign your license rights to any other person in any manner
              (by assignment, operation of law or otherwise) unless you have obtained written
              consent from Conviva. If you attempt to transfer or assign any of your license rights
              without Conviva’s consent, the transfer or assignment will be ineffective, null, and
              void (and you will be in material breach of these Terms).
            </li>
            <li>
              Free Trials. Conviva may offer Services on a free trial basis (“Free Trial”) for a
              period of time specified at Conviva’s discretion, or if none stated, then thirty (30)
              days starting from the date you have been activated for the applicable trial. If
              Conviva offers you a Free Trial, the specific terms of the Free Trial will be provided
              at the time of signup and/or in the promotional materials describing the Free Trial
              (if any). Your Free Trial is subject to the terms provided therein and the terms of
              this Agreement. Conviva reserves the right to modify or terminate the Free Trial at
              any time, with or without notice, and in its sole discretion.
            </li>
          </ul>
        </p>

        <p>
          THE SERVICES AND CONVIVA CONFIDENTIAL INFORMATION, INCLUDING WITHOUT LIMITATION THE
          LICENSED MATERIALS, ARE PROVIDED &ldquo;AS IS&rdquo; AND CONVIVA MAKES NO WARRANTY
          EXPRESS, IMPLIED, STATUTORY OR OTHERWISE, REGARDING THE SAME AND SPECIFICALLY DISCLAIMS
          ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
          NONINFRINGEMENT, OR THOSE ARISING FROM A COURSE OF DEALING OR USAGE OF TRADE.
        </p>

        <p>
          CONVIVA&apos;S TOTAL LIABILITY FOR DIRECT DAMAGES ARISING OUT OF OR RELATING TO THESE
          TERMS AND YOUR ACCESS TO AND USE OF THE SERVICES AND CONVIVA CONFIDENTIAL INFORMATION,
          INCLUDING WITHOUT LIMITATION THE LICENSED MATERIALS, SHALL NOT EXCEED ONE THOUSAND US
          DOLLARS ($1,000). NOTWITHSTANDING THE FOREGOING, IF YOU USE OUR SERVICES ON A FREE TRIAL
          BASIS, THEN CONVIVA’S TOTAL LIABILITY FOR DIRECT DAMAGES ARISING OUT OF OR RELATING TO
          THESE TERMS AND YOUR ACCESS TO AND USE OF THE FREE TRIAL SERVICES SHALL NOT EXCEED ONE
          HUNDRED US DOLLARS ($100). UNDER NO CIRCUMSTANCES SHALL CONVIVA BE LIABLE FOR ANY SPECIAL,
          INCIDENTAL OR CONSEQUENTIAL DAMAGES ARISING IN ANY WAY OUT OF THESE TERMS OR YOUR ACCESS
          TO OR USE OF THE SERVICES OR CONVIVA CONFIDENTIAL INFORMATION, INCLUDING WITHOUT
          LIMITATION THE LICENSED MATERIALS, HOWEVER CAUSED, WHETHER ARISING UNDER A THEORY OF
          CONTRACT, TORT (INCLUDING NEGLIGENCE OR OTHERWISE), INCLUDING, WITHOUT LIMITATION, DAMAGES
          FOR LOST PROFITS, LOSS OF DATA, OR COSTS OF PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES.
        </p>

        <p>
          As mentioned in Section 3 above, Conviva reserves the right to terminate any and all of
          the Services, and the license to any and all of the Licensed Materials, provided to you or
          anyone else under these Terms at any time for any or no reason and shall give you notice
          thereof. In addition, Conviva also reserves the right to suspend or terminate the program
          under which it provides any or all of the Services and/or Licensed Materials generally to
          its customers and/or licensees for any or no reason. Upon termination, you shall, at
          Conviva’s discretion, destroy or return to Conviva all Licensed Materials and other
          Conviva confidential information, including all copies thereof, whether in written or
          other tangible form, provided to you pursuant to these Terms. You shall provide Conviva
          with written notice of such return or destruction within thirty (30) days following any
          such termination.
        </p>

        <h2>7. CONVIVA PROPRIETARY RIGHTS</h2>
        <p>
          All contents of the Services, including but not limited to the logo, design, text,
          libraries, software development kits, software, configurations, graphics, Licensed
          Materials, other files, and their selection and arrangement and any related pricing
          information, are the confidential information of Conviva and belong to Conviva and/or its
          suppliers, affiliates, or licensors. Conviva or its licensors own and reserve all right,
          title and interest in and to the Services and all hardware, software and other items used
          to provide the Services, other than the rights we expressly grant to you to use the
          Services and Conviva confidential information. No title to or ownership of any proprietary
          rights related to the Services or Conviva confidential information is transferred to you
          pursuant to these Terms. You acknowledge and agree that any misuse of the Services or
          Conviva confidential information shall be deemed a material breach hereof. If you provide
          comments, suggestions and recommendations to Conviva about a Service (e.g., modifications,
          enhancements, improvements) (collectively, <b>&ldquo;Feedback&rdquo;</b>), you are
          automatically assigning this Feedback to Conviva.
        </p>

        <h2>8. ACCEPTABLE USE POLICY</h2>
        <p>
          You agree you will not, nor will you encourage others or assist others, to harm the
          Services or use the Services to harm or harass others. For example, you must not use the
          Services to harm, threaten, or harass another person, organization or Conviva and/or to
          build a similar service or website. You must not: (a) damage, disable, overburden, or
          impair the Service (or any network connected to the Services); (b) resell or redistribute
          the Services or any part of it; (c) use any unauthorized means to modify, reroute, or gain
          access to the Services or attempt to carry out these activities; (d) use any automated
          process or service (such as a bot, a spider, or periodic caching of information stored by
          Conviva) to access or use the Services; (e) use the Services beyond the features
          allocation and amounts provided in that Service or in violation of our fair use policy; or
          (f) use the Services to violate any law or distribute malware.
        </p>

        <p>
          As part of our efforts to protect the Services, protect our customers, or to stop you from
          breaching these Terms, we reserve the right to block or otherwise prevent you from using
          the Services if we reasonably suspect that fraud, corporate espionage or other
          unauthorized access or use is associated with your Conviva account or use of the Services,
          for any reason, whether or not due to your fault.
        </p>

        <h2 id="privacy_policy">9. PRIVACY </h2>
        <p>
          <b>a. </b> In order to operate, provide and improve our Services, we collect certain
          information about you. We explain the types of information we collect, including how its
          stored, used, processed and protected in our{' '}
          <a href="https://www.conviva.com/legal/">Privacy Policy</a>. You acknowledge your use of
          the Services is subject to our Privacy Policy.
        </p>

        <p>
          With the use of certain Services, we may also collect data elements that, whether alone or
          in combination, may be Personal Information. We handle Personal Information only as
          outlined in the terms of our agreements with our Customers (where applicable), these Terms
          and our Privacy Policy.
        </p>

        <p>
          If you collect, access or view Personal Information through your use of the Services, you
          acknowledge and agree that you are responsible for treating all Personal Information
          consistent with Data Protection Laws. Through the use of the Services, you also
          acknowledge and agree that you will not share or disclose Personal Information for any
          purpose other than the purpose for which the Services are intended and that you have
          obtained all necessary consents and authorizations to allow Conviva to collect, process
          and store Personal Information.
        </p>

        <p>
          <b>b. </b>
          <u>Additional terms for the United Kingdom or EU Country Members</u>
        </p>
        <ol type="i" style={{ fontWeight: 'bold' }}>
          <li style={{ fontWeight: 'normal' }}>
            <p>
              Sections 9(b)(i), 9(b)(ii), 9(b)(iii) and 9(c) of these Terms apply in the event that
              an EU Directive and Legislation, the GDPR, UK GDPR and/or DPA 2018 apply(ies) to these
              Terms and/or the Services provided hereunder. If Conviva processes Personal
              Information on your behalf for purposes of you receiving Services, as between you and
              Conviva, you are the data controller and Conviva is the data processor in relation to
              the processing of any such Personal Information. Conviva shall: (i) only process
              Personal Information for the duration of the Agreement; (ii) only process Personal
              Information on your behalf to provide the Services; (iii) only process Personal
              Information on and in accordance with your instructions and as set forth in this
              Agreement; (iv) keep Personal Information confidential and ensure that appropriate
              technical and organizational measures are taken to protect against the accidental,
              unauthorized or unlawful destruction, loss, alteration, access or disclosure of such
              Personal Information; (v) not transfer Personal Information outside the EEA” to
              countries whose laws the European Union has acknowledged do not ensure an adequate
              level of data privacy protection, without your prior written consent; (vi) not
              transfer Personal Information outside the United Kingdom to countries whose laws the
              United Kingdom has acknowledged do not ensure an adequate level of data privacy
              protection, without your prior written consent; (vii) delete or return the Personal
              Information according to your instructions (except to the extent that Conviva is
              required to continue to store the Personal Information); (viii) ensure that all its
              personnel who have access to Personal Information are subject to obligations of
              confidentiality when processing such Personal Information; (ix) promptly inform you if
              any Personal Information is (while within the Conviva’s possession or control) subject
              to a Personal Information breach (as defined in the Data Protection Laws); (x) provide
              you and any DP Regulator all information and assistance necessary to demonstrate
              compliance with the obligations in this Agreement; and (xi) permit your chosen
              independent auditor (at your sole cost) to access any relevant premises, personnel or
              records of Conviva on no less than 30 days prior written notice to audit and verify
              compliance with Conviva’s data protection obligations under this Agreement.
            </p>
            <p>
              For purposes of Section 9(b), the parties acknowledge and agree that the provision of
              the Services may involve a transfer of Personal Information processed in accordance
              with the GDPR from you to Conviva, its Affiliates (if any) and Third-Party
              Subprocessors engaged in the provision of the Services and located outside the EEA
              (“ex-EEA Transfer”). In respect of such ex-EEA Transfers and where no Alternative
              Adequate Level of Protection applies, Section 9(b)(ii) shall apply and each party
              shall maintain records of all processing operations under its responsibility that
              contain at least the minimum information required by the Data Protection Laws, and
              (iii) shall make such information available to any DP Regulator on request. For
              purposes of Section 9(b), the parties acknowledge and agree that the provision of the
              Services may involve a transfer of Personal Information processed in accordance with
              the UK GDPR and the DPA 2018 from you to Conviva, its Affiliates (if any) and
              Third-Party Subprocessors engaged in the provision of the Services and located outside
              the United Kingdom (ex-UK Transfer”). In respect of such ex-UK Transfers and where no
              Alternative Adequate Level of Protection applies, Section 9(b)(iii) shall apply and
              each Party shall maintain records of all processing operations under its
              responsibility that contain at least the minimum information required by the Data
              Protection Laws, and shall make such information available to any DP Regulator on
              request.
            </p>
            <p>
              In the event of any conflict or inconsistency between this Terms and the Standard
              Contractual Clauses, the Standard Contractual Clauses shall prevail to the extent of
              that conflict or inconsistency.
            </p>
            <p>
              In respect of any ex-EEA Transfers or ex-UK Transfers, the following supplementary
              measures shall apply: (i) Conviva represents and warrants that, at the time of the
              transfer, it has not received any formal legal requests from any government
              intelligence or agencies in the country to which the relevant Personal Information is
              being exported, for access to (or for copies of) Personal Information that has been
              transferred to Conviva pursuant to these Terms (&ldquo;Government Agency
              Requests&rdquo;); (ii) if, after the date of these Terms, Conviva receives any
              Government Agency Requests which concerns Personal Information, it will (unless
              prohibited by law from doing so) inform customer in writing as soon as reasonably
              practicable and customer and Conviva shall (as soon as reasonably practicable) discuss
              and determine (as permitted under law) whether all or any transfers of Personal
              Information pursuant to these Terms should be suspended in the light of the such
              Government Agency Requests; and (iii) Customer and Conviva will meet at the Customer’s
              reasonable request as necessary to review whether: (a) the protection afforded by the
              laws in which Conviva is located to data subjects whose Personal Information is being
              transferred is sufficient to provide broadly equivalent protection to that afforded in
              the EEA and the UK; (b) additional measures are reasonably necessary to enable the
              transfer to be compliant with the Data Protection Laws; and (c) it is still
              appropriate for Personal Information to be transferred to Conviva, taking into account
              all relevant information available to the parties, together with guidance provided by
              the DP Regulators.
            </p>
          </li>
          <li style={{ fontWeight: 'normal' }}>
            <p>Ex-EEA Transfers.</p>
            <ol type="a" style={{ paddingLeft: '10px', fontWeight: 'bold' }}>
              <li style={{ fontWeight: 'normal' }}>
                &emsp; The provisions of this Section (ii) shall apply only in respect of Personal
                Information which is subject to the regulation of the GDPR.
              </li>
              <li style={{ fontWeight: 'normal' }}>
                &emsp; The ex-EEA Transfer shall be governed by the EU SCCs and references in the EU
                SCCs and in this Section 9(b)(ii) to the data exporter shall be Customer and
                references to the data importer shall be Conviva.
              </li>
              <li style={{ fontWeight: 'normal' }}>
                &emsp; The EU SCCs are hereby incorporated into these Terms with the following
                amendments (with references in this Section 9(b)(ii)(c) to Clauses being to Clauses
                of the EU SCCs):
                <ol type="i" style={{ paddingLeft: '10px', fontWeight: 'bold' }}>
                  <li style={{ fontWeight: 'normal' }}>
                    &emsp; All footnotes and explanatory notes in the EU SCCs are deleted;
                  </li>
                  <li style={{ fontWeight: 'normal' }}>
                    &emsp; As the ex-EEA Transfer is a Data Controller to Data Processor transfer,
                    only the provisions relating to Module 2 apply to such ex-EEA Transfer, and the
                    provisions relating only to Modules 1, 3 and 4 are deleted and shall not apply
                    to such ex-EEA Transfer;
                  </li>
                  <li style={{ fontWeight: 'normal' }}>
                    &emsp; In respect of Clause 7 (docking clause), this clause is not used;
                  </li>
                  <li style={{ fontWeight: 'normal' }}>
                    &emsp; In respect of Clause 9 (sub-processors), &apos;Option 2: General Written
                    Authorisation&apos; applies, and the minimum time period for the data importer
                    to specifically inform the data exporter in writing of any intended changes to
                    that list in accordance with Clause 9 shall be thirty (30) days;
                  </li>
                  <li style={{ fontWeight: 'normal' }}>
                    &emsp; In respect of Clause 13(a) (supervision), the following wording shall
                    apply: The supervisory authority with responsibility for ensuring compliance by
                    the data exporter with Regulation (EU) 2016/679 as regards the data transfer, as
                    indicated in Annex I.C, shall act as competent supervisory authority;
                  </li>
                  <li style={{ fontWeight: 'normal' }}>
                    &emsp; In respect of Clause 17 (governing law), Option 2 shall apply and the
                    Clauses shall be governed by the law of the EU Member State in which the data
                    exporter is established. Where such law does not allow for third-party
                    beneficiary rights, they shall be governed by the law of another EU Member State
                    that does allow for third-party beneficiary rights. The parties agree that this
                    shall be the law of Ireland;
                  </li>
                  <li style={{ fontWeight: 'normal' }}>
                    &emsp; In respect of Clause 18 (choice of forum and jurisdiction), the relevant
                    courts shall be those of the EU Member State whose law governs the Clauses in
                    accordance with Clause 17.
                  </li>
                </ol>
              </li>
              <li style={{ fontWeight: 'normal' }}>
                &emsp; Annex I of the EU SCCs shall be completed with the information set out in
                Annex 1 of these Terms.
              </li>
              <li style={{ fontWeight: 'normal' }}>
                &emsp; Annex II of the EU SCCs shall be completed with the information set out in
                Annex 2 of these Terms.
              </li>
              <li style={{ fontWeight: 'normal' }}>
                &emsp; Annex III of the EU SCCs shall be completed with the information set out in
                Annex 3 of these Terms.
              </li>
            </ol>
          </li>
          <li style={{ fontWeight: 'normal' }}>
            <p>Ex-UK Transfers</p>
            <p>
              <b>a.</b> &emsp; The provisions of this Section 9(b)(iii) shall apply only in respect
              of Personal Information which is subject to the regulation of the UK GDPR and DPA
              2018.
            </p>
            <p>
              <b>b.</b> &emsp; The ex-UK Transfer shall be governed by the UK SCCs which are hereby
              incorporated into this Section 9(b)(iii) with the following amendments (with
              references in this Section 9(b)(iii) to Clauses being to Clauses of the UK SCCs) and
              references in the UK SCCs and in this Section 9(b)(iii) to the data exporter shall be
              customer and references to the data importer shall be Conviva.{' '}
            </p>
            <p>
              <b>c.</b> &emsp; Table 1 of the UK SCCs shall be completed as follows:{' '}
            </p>
            <p>
              &emsp; (i) The parties’ details shall be the parties and their affiliates to the
              extent any of them are involved in such a transfer, including those set forth in Annex
              1 of these Terms.
            </p>
            <p>
              &emsp; (ii) The Key Contact shall be the contacts set forth in Annex 1 of these Terms.
            </p>
            <p>
              <b>d.</b> &emsp; Table 2 of the UK SCCs shall be completed as follows: the Approved EU
              SCCs referenced in Table 2 shall be the EU SCCs as set forth in Section 9(b)(ii) of
              these Terms.{' '}
            </p>
            <p>
              <b>e.</b> &emsp; Table 3 of the UK SCCs shall be completed as follows: Annexes 1A and
              1B shall be set forth in Annex 1 of these Terms. Annex II shall be set forth in Annex
              2 of these Terms. Annex III shall be set forth in Annex 3 of these Terms.
            </p>
            <p>
              <b>f.</b> &emsp; Table 4 of the UK SCCs shall be completed as follows: Conviva may end
              this Section 9(c) as set out in Section 19 of the UK SCCs.
            </p>
          </li>
        </ol>

        <p>
          <b>c. </b>
          <u>Additional Definitions.</u>
        </p>

        <p>
          “Affiliate” means an entity that is controlled by, controls or is under common control
          with a party, where “control” means the direct or indirect ownership of more than fifty
          percent (50%) of the shares or interests entitled to vote for the directors thereof or the
          equivalent, for so long as such entitlement exists, or equivalent power over management
          thereof.
        </p>

        <p>
          “Alternative Adequate Level of Protection” means: (i) the country where Conviva or a
          Third-Party Subprocessor is located is recognized by the European Union (if the Personal
          Information is transferred from the EEA) and/or the United Kingdom (if the Personal
          Information is transferred from the United Kingdom) to have an adequate level of
          protection of personal data as described in the Data Protection Laws; or (ii) Conviva or
          the Third-Party Subprocessor has fully implemented binding corporate rules which provide
          adequate safeguards as required by the Data Protection Laws; or (iii) Conviva or the
          Third-Party Subprocessor has implemented any other similar program, or appropriate
          safeguards that are recognized by the European Union (if the Personal Information is
          transferred from the EEA) and/or the United Kingdom (if the Personal Information is
          transferred from the United Kingdom) or by the Data Protection Laws or applicable DP
          Regulator as providing an adequate level of protection.
        </p>

        <p>
          “Data Protection Laws” means any laws and regulations applicable in any relevant
          jurisdiction relating to privacy or the use or processing of data relating to natural
          persons, including without limitation: (a) any state or federal laws or regulations in the
          United States including without limitation the CCPA; (b) EU Directive 2002/58/EC (as
          amended by 2009/139/EC) and any legislation implementing or made pursuant to such
          directive, including (in the UK) the Privacy and Electronic Communications (EC Directive)
          Regulations 2003; (c) GDPR; (d) UK GDPR; (e) the DPA 2018; and (f) any laws or regulations
          ratifying, implementing, adopting, supplementing or replacing GDPR, UK GDPR and/or the DPA
          2018; in each case, to the extent in force, and as such are updated, amended or replaced
          from time to time.
        </p>

        <p>“DPA 2018” means the Data Protection Act 2018 in the UK.</p>

        <p>
          “DP Regulator” means any governmental or regulatory body or authority with responsibility
          for monitoring or enforcing compliance with the Data Protection Laws.
        </p>

        <p>“EEA” means the European Economic Area.</p>

        <p>
          “EU SCCs” means the standard contractual clauses approved by the European Commission in
          Commission Decision 2021/914 dated 4 June 2021, for transfers of personal data in
          countries not otherwise recognised as offering an adequate level of protection for
          personal data by the European Commission (as amended and updated from time to time).
        </p>

        <p>“Ex-EEA Transfer” means as defined in Section 9(b)(i).</p>

        <p>“Ex-UK Transfer” means as defined in Section 9(b)(i).</p>

        <p>
          “Personal Information” means data or information that, alone or in combination with other
          data or information collected, received or processed by the Services, is considered
          “personal data,” “personal information,” “personally identifiable information” or other
          such similar term as defined under the applicable Data Protection Laws, including but not
          limited to Credentials.
        </p>

        <p>“Standard Contractual Clauses” means the EU SCCs and/or the UK SCCs.</p>

        <p>
          “Third-Party Subprocessors” means the sub-processors listed on Conviva’s website at{' '}
          <a href="https://www.conviva.com/conviva-subprocessors/">
            https://www.conviva.com/conviva-subprocessors/
          </a>
          .
        </p>

        <p>
          “UK GDPR” means the GDPR as it forms part of the law of England and Wales, Scotland and
          Northern Ireland by virtue of section 3 of the European Union (Withdrawal) Act 2018 in the
          UK.
        </p>

        <p>
          “UK SCCs” means the United Kingdom Information Commissioner’s International Data Transfer
          Addendum to the EU Commission Standard Contractual Clauses, Version B1.0, in force 21
          March 2022.
        </p>

        <p>
          In these Terms, the terms “data subject“, “personal data“, “data controller“, “data
          processor” and “processing” shall have the meanings set out in the applicable Data
          Protection Laws.
        </p>

        <h2>10. MISCELLANEOUS</h2>
        <p>
          You must comply with all domestic and international laws and regulations that apply to
          your use of the Services. The prevailing party in any litigation in connection with these
          Terms shall be entitled to an award of reasonable attorneys’ fees and costs. If, for any
          reason a court of competent jurisdiction finds any provision of these Terms, or portion
          thereof, to be unenforceable, that provision will be enforced to the maximum extent
          permissible so as to affect the intent of the parties, and the remainder of these Terms
          will continue in full force and effect. These Terms shall be governed by and construed
          under the laws of the State of California, USA, without regard for its conflict of laws
          principles. In no event shall these Terms be governed by the United Nations Convention on
          Contracts for the International Sale of Goods. These Terms and the applicable Customer
          Agreement (if any) constitute the entire agreement between the parties regarding the
          subject matter hereof and supersede all prior agreements and representations, oral or
          written, express or implied, regarding such subject matter.
        </p>

        <h1 style={{ textAlign: 'center', fontSize: '1.4rem', fontWeight: 300 }}>ANNEX 1</h1>
        <ol type="A">
          <li>
            <b>
              <h2>LIST OF PARTIES</h2>
            </b>
            <ol type="1">
              <li>
                <p>
                  <b>Data exporter(s):</b>
                </p>
                <p>
                  Name: As set out in the Order Form (or as otherwise notified in writing by the
                  data exporter). <br />
                  Address: As set out in the Order Form (or as otherwise notified in writing by the
                  data exporter). <br />
                  Contact person&apos;s name, position and contact details: As set out in the Order
                  Form (or as otherwise notified in writing by the data exporter). <br />
                  Activities relevant to the data transferred under these Clauses: Data
                  exporter&apos;s online video services are integrated with the data importer&apos;s
                  software and services. <br />
                  Signature and date: Executed in accordance with the first paragraph of these
                  Terms.
                  <br />
                  Role (controller/processor): Controller.
                  <br />
                </p>
              </li>
              <li>
                <p>
                  <b>Data importer:</b>
                </p>
                <p>
                  Name: Conviva Inc. <br />
                  Address: 989 E. Hillsdale Blvd., 4th Floor, Foster City, CA 94404, USA. <br />
                  Contact person&apos;s name, position and contact details: Bradley M. Kancigor,
                  General Counsel, +1 (650) 401-8282, legal@conviva.com <br />
                  Activities relevant to the data transferred under these Clauses: Data importer
                  provides software products and SaaS-based services for analyzing and optimizing
                  the quality of data exporter&apos;s online video streaming services. <br />
                  Signature and date: Executed in accordance with the first paragraph of these
                  Terms. <br />
                  Role (controller/processor): Processor. <br />
                </p>
              </li>
            </ol>
          </li>
          <li>
            <b>
              <h2>DESCRIPTION OF TRANSFER</h2>
            </b>
            <p>
              <i>Categories of data subjects whose personal data is transferred:</i>
            </p>
            <p>
              Data exporter’s end users that use data exporter’s online video services that are
              integrated with the data importer’s services; for the data importer’s Social Insights
              service, data exporters’ end users that post videos and other content on social
              platform services, from which data importer obtains data and analytics via API calls;
              and for data importer’s Application Insights service, data exporter’s end users that
              use data exporter’s websites, applications and other services that are integrated with
              data importer’s services.
            </p>
            <p>
              Data exporter’s employees that log into the data importer’s website to access the data
              and analytics computed by the data importer’s services.
            </p>
            <p>
              <i>Categories of personal data transferred:</i>
            </p>
            <p>
              Data exporter&apos;s end users: IP addresses and, at data exporter&apos;s discretion,
              viewer and/or subscriber identifiers (such as viewer ID, subscriber ID, household ID
              and profile ID), device advertising identifiers (such as Google Advertising ID (GAID),
              Apple ID for Advertising (IDFA), Roku ID, Samsung ID and Vizio ID), geolocation
              information (such as latitude and longitude), and other personal data related to data
              exporter&apos;s end users&apos; use of data exporter&apos;s online video services; for
              data importer&apos;s Social Insights services, the captions, comments and/or tweets
              that contain personal data, and other personal data and/or identifiers related to data
              exporter&apos;s use of the social platform services; and for data importer&apos;s
              Application Insights service, IP addresses, user or subscriber identifiers, and other
              personal data related to data exporter&apos;s end users&apos; use of data
              exporter&apos;s websites, applications and other services that are integrated with
              data importer&apos;s services.
            </p>
            <p>
              Data exporter&apos;s employees: Credentials including name, email address and
              password. <br />
              Sensitive data transferred (if applicable) and applied restrictions or safeguards that
              fully take into consideration the nature of the data and the risks involved, such as
              for instance strict purpose limitation, access restrictions (including access only for
              staff having followed specialised training), keeping a record of access to the data,
              restrictions for onward transfers or additional security measures.
            </p>
            <p>Not applicable.</p>
            <p>
              <i>
                The frequency of the transfer (e.g. whether the data is transferred on a one-off or
                continuous basis).
              </i>
            </p>
            <p>
              Data will be transferred on a continuous basis as required by the data exporter to
              receive the data importer&apos;s services.
            </p>
            <p>
              <i>Nature of the processing</i>
            </p>
            <p>
              The Conviva Experience and Viewer Insights Services provided by the data importer are
              passive, statistical analytics solution, with Conviva software libraries and/or APIs
              integrated by data exporter as a plug-in to data exporter’s video player(s). These
              services enable quality of experience and viewer insights into video playback on data
              exporter’s video players. When a video stream is initiated, the data importer’s
              software computes raw technical information at the player level into service
              statistics and transmits these service statistics to the data importer’s SaaS
              platform, for data exporter to view in data importer’s front-end interface, Pulse,
              according to certain metrics and filters.
            </p>
            <p>
              The Conviva Application Insights Service provided by the data importer provides
              real-time analytics of application experience and engagement and includes capabilities
              to automatically capture events, clean and normalize the ingested data, associate
              semantics, and report precise, comprehensive and consistent viewer experience across
              any device and application.
            </p>
            <p>
              The Conviva Social Insights Service provided by the data importer is an analytics
              solution that provides consumption and audience intelligence for video playback on
              social platforms. Through API calls to social platforms (including Facebook,
              Instagram, YouTube, Twitter and Snapchat), metrics and metadata are obtained regarding
              videos posted by data exporter on these social platforms.
            </p>
            <p>
              The Conviva Precision Service provided by the data importer is an active solution,
              with Conviva Precision software libraries and/or APIs integrated by the data exporter
              as a plug-in to data exporter’s video player(s). This service optimizes quality of
              experience of video playback on data exporter’s video player(s) using the Experience
              Insights service statistics. Such optimization occurs by choosing a suitable CDN
              provider and optimal bitrate and resolution for the video playback under current
              technical and other circumstances prevailing at the time in the relevant ecosystem.
            </p>
            <p>
              Data exporter’s employees may access the applicable service statistics, metrics and
              metadata by securely logging into the data importer’s website. Such Credentials
              include name, email address and password.
            </p>
            <p>
              <i>Purpose(s) of the data transfer and further processing</i>
            </p>
            <p>
              The purpose of the data transfer and further processing is for the data importer to
              provide software products and SaaS-based services as described above and in the
              Customer Agreement.
            </p>
            <p>
              The period for which the personal data will be retained, or, if that is not possible,
              the criteria used to determine that period
            </p>
            <p>
              The data importer retains personal data collected from the data exporter in connection
              with its use of Conviva’s services, such as account Credentials, for as long as the
              data exporter’s account is active or as needed for the data importer to provide the
              services to the data exporter. If any personal data is collected from the data
              exporter’s end users (such as IP addresses and viewer ID numbers), then the data
              importer will retain this personal data for up to thirteen (13) months after
              collection, unless otherwise directed by the data exporter and/or as needed for system
              backup archival use. More information about Conviva’s Data Access and Retention Policy
              can be found in the help drop-down menu of Conviva’s Insights web portal.
            </p>
            <p>
              <i>
                For transfers to (sub-) processors, also specify subject matter, nature and duration
                of the processing
              </i>
            </p>
            <p>
              The data importer uses various sub-processors to support the processing carried out as
              described above.
            </p>
          </li>
          <li>
            <b>
              <h2>COMPETENT SUPERVISORY AUTHORITY</h2>
            </b>
            <p>
              Identify the competent supervisory authority/ies in accordance with Clause 13: The
              supervisory authority of the EU Member State in which the data exporter is
              established.
            </p>
          </li>
        </ol>
        <h1 style={{ textAlign: 'center', fontSize: '1.4rem', fontWeight: 300 }}>ANNEX 2</h1>
        <h1 style={{ textAlign: 'center', fontSize: '1.4rem', fontWeight: 300 }}>
          TECHNICAL AND ORGANISATIONAL MEASURES INCLUDING TECHNICAL AND ORGANISATIONAL MEASURES TO
          ENSURE THE SECURITY OF THE DATA
        </h1>
        <p>
          Description of the technical and organizational security measures implemented by the data
          importer in accordance with the Standard Contractual Clauses:
        </p>
        <ul>
          <li>
            The data importer (Conviva) manages and maintains its internal systems, processes,
            security and system alerts pursuant to industry best practices and is ISO/IEC 27001:2013
            certified. All personal data collected by the data importer is transmitted over SSL and
            stored using logical separation for each Customer. Encryption is used to transmit data
            from endpoint devices to the data importer. The only ports open for inbound data are
            HTTP (80) and HTTPS (443) for the data importer’s SaaS application. Requests to the HTTP
            endpoint are redirected to the HTTPS endpoint to enforce client security. Any personal
            data stored in the database is encrypted utilizing AES encryption and SHA-256 hashes
            where possible. Industry best practice access controls, both physical and virtual, are
            maintained by the data importer and its vendors for data center hosting and data access.
            Administrative access is performed over SSH using private keys, and/or SSL for web
            portals, using passwords. Administrator accounts are granted only to those who need
            privileged access and revoked once it is no longer needed. Further, the data importer’s
            website used to access its SaaS application employs industry best practices for secure
            authentication. User access is via single sign-on and multi-factor authentication, built
            in a high-availability configuration, and passwords are converted to irreversible and
            unique hashes.
          </li>
          <li>
            In addition, the data exporter (Customer) shall cooperate with the data importer in
            establishing a password or other procedures for verifying that only designated employees
            of the data exporter have access to any administrative functions of the data importer’s
            services. The data exporter will be responsible for maintaining the security of its
            account, passwords (including administrative and user passwords), files, and for all
            uses of the data exporter’s account. The data exporter shall not share with any third
            party (other than its Affiliates) any such account or password without the prior written
            consent of the data importer.
          </li>
          <li>
            Additional details regarding Conviva’s security measures can be found at{' '}
            <a href="https://www.conviva.com/security/">https://www.conviva.com/security/</a>
          </li>
        </ul>
        <h1 style={{ textAlign: 'center', fontSize: '1.4rem', fontWeight: 300 }}>ANNEX 3</h1>
        <h1 style={{ textAlign: 'center', fontSize: '1.4rem', fontWeight: 300 }}>
          LIST OF SUB-PROCESSORS
        </h1>
        <p>
          The controller has authorised the use of the sub-processors listed at{' '}
          <a href="https://www.conviva.com/conviva-subprocessors/">
            https://www.conviva.com/conviva-subprocessors/
          </a>
          .
        </p>
      </Content>
      <Footer />
    </LegalContainer>
  );
};

export default Legal;
